// src/hooks/useSocket.jsx
import { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import { initializeSocket } from '../utils/urlConfig';
import logger from '../utils/logger';

export const useSocket = (isAuthenticated) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [runningSyncs, setRunningSyncs] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use a ref to keep track of current runningSyncs without triggering effect reruns
  const runningSyncsRef = useRef(runningSyncs);
  runningSyncsRef.current = runningSyncs;

  useEffect(() => {
    let socketInstance = null;
    
    if (isAuthenticated) {
      // Get the token from localStorage for socket authentication
      const token = localStorage.getItem('token');
      
      // Only proceed if we have a valid token
      if (token) {
        logger.log('Initializing socket connection with token');
        socketInstance = initializeSocket(io, token);
        setSocket(socketInstance);
        
        // Connection event handlers with improved reconnection handling
        socketInstance.on('connect', () => {
          logger.log('Socket connected successfully');
          setIsConnected(true);
          setError(null);
          toast.success('Connected to sync server');
        });
        
        socketInstance.on('disconnect', (reason) => {
          logger.log(`Socket disconnected. Reason: ${reason}`);
          setIsConnected(false);
          
          // Handle different disconnect reasons
          let message;
          let shouldShowToast = true;
          
          switch (reason) {
            case 'io server disconnect':
              message = 'Server disconnected the socket. This may be due to server maintenance.';
              break;
            case 'io client disconnect':
              message = 'Client disconnected from server.';
              shouldShowToast = false; // Don't show toast for manual disconnects
              break;
            case 'ping timeout':
              message = 'Connection timed out. Attempting to reconnect...';
              break;
            case 'transport close':
              message = 'Connection transport closed. Reconnecting...';
              break;
            case 'transport error':
              message = 'Connection transport error. Reconnecting...';
              break;
            default:
              message = `Disconnected from sync server (${reason}). Trying to reconnect...`;
          }
          
          setError(message);
          if (shouldShowToast) {
            toast.warn(message);
          }
        });
        
        // Add reconnection attempt handlers
        socketInstance.on('reconnect_attempt', (attemptNumber) => {
          logger.log(`Reconnection attempt ${attemptNumber}`);
          setError(`Reconnecting... (attempt ${attemptNumber})`);
        });
        
        socketInstance.on('reconnect', (attemptNumber) => {
          logger.log(`Successfully reconnected after ${attemptNumber} attempts`);
          setIsConnected(true);
          setError(null);
          toast.success(`Reconnected to sync server (${attemptNumber} attempts)`);
        });
        
        socketInstance.on('reconnect_error', (error) => {
          logger.error('Reconnection error:', error);
          setError(`Reconnection failed: ${error.message || 'Unknown error'}`);
        });
        
        socketInstance.on('reconnect_failed', () => {
          logger.error('Failed to reconnect after maximum attempts');
          setError('Failed to reconnect to sync server. Please refresh the page.');
          toast.error('Failed to reconnect to sync server. Please refresh the page.');
        });
        
        // Data event handlers
        socketInstance.on('sync:initialStatus', (data) => {
          logger.log('Received initial sync status:', data);
          logger.log('Sync history logs available:', data?.history?.map(sync => ({
            id: sync.id,
            type: sync.type,
            hasLogs: !!(sync.logs && sync.logs.length > 0),
            logsCount: sync.logs?.length || 0
          })));
          if (data && data.running) setRunningSyncs(data.running || []);
          if (data && data.history) setSyncHistory(data.history || []);
          setError(null);
          setLoading(false);
        });
        
        socketInstance.on('sync:statusUpdate', (data) => {
          logger.log('Received sync status update:', data);
          if (data && data.running) setRunningSyncs(data.running || []);
          if (data && data.history) setSyncHistory(data.history || []);
          setLoading(false);
        });
        
        socketInstance.on('sync:started', (syncInfo) => {
          logger.log('Sync started:', syncInfo);
          // Add the new sync to the running syncs
          setRunningSyncs(prev => [...prev, syncInfo]);
          toast.info(`Sync started: ${syncInfo.type} (${syncInfo.id.substring(0, 8)})`);
        });
        
        socketInstance.on('sync:progress', (syncInfo) => {
          logger.log('Sync progress update received:', syncInfo);
          
          // Make sure the sync info contains the expected fields
          if (!syncInfo || !syncInfo.id) {
            logger.error('Received invalid sync progress update:', syncInfo);
            return;
          }
          
          // Use the ref for logging instead of the state directly
          logger.log('Current running syncs before update:', runningSyncsRef.current.map(s => s.id));
          
          // Update running syncs array when receiving progress updates
          setRunningSyncs(prev => {
            // Check if this sync is already in the list
            const existingIndex = prev.findIndex(sync => sync.id === syncInfo.id);
            
            // Special handling for mentor-sync type
            if (syncInfo.type === 'mentor-sync') {
              // Ensure progress object is properly structured for the frontend
              syncInfo.progress = {
                ...syncInfo.progress,
                processed: syncInfo.progress?.processed || 0,
                total: syncInfo.progress?.total || 0,
                // Get values from either direct progress or from nested mentors object
                created: syncInfo.progress?.created || syncInfo.results?.mentors?.created || syncInfo.mentors?.created || 0,
                updated: syncInfo.progress?.updated || syncInfo.results?.mentors?.updated || syncInfo.mentors?.updated || 0,
                failed: syncInfo.progress?.failed || syncInfo.results?.mentors?.failed || syncInfo.mentors?.failed || 0
              };
              
              logger.log('Processed mentor-sync progress:', syncInfo.progress);
            }
            
            if (existingIndex >= 0) {
              // Replace the existing sync with updated info
              logger.log(`Updating existing sync at index ${existingIndex}`);
              const newArray = [...prev];
              newArray[existingIndex] = syncInfo;
              return newArray;
            } else {
              // This is a new sync we haven't seen before - add it
              logger.log(`Adding new sync: ${syncInfo.id}`);
              return [...prev, syncInfo];
            }
          });
        });
        
        socketInstance.on('sync:completed', (syncInfo) => {
          logger.log('Sync completed:', syncInfo);
          
          // Remove the sync from running syncs
          setRunningSyncs(prev => prev.filter(sync => sync.id !== syncInfo.id));
          
          // Add appropriate toast notification
          if (syncInfo.status === 'completed') {
            toast.success(`Sync completed: ${syncInfo.type} (${syncInfo.id.substring(0, 8)})`);
          } else {
            toast.error(`Sync failed: ${syncInfo.type} (${syncInfo.id.substring(0, 8)})`);
          }
        });
        
        socketInstance.on('connect_error', (err) => {
          logger.error('Socket connection error:', err);
          setError('Failed to connect to sync server: ' + (err.message || 'Unknown error'));
          toast.error('Failed to connect to sync server: ' + (err.message || 'Unknown error'));
          setIsConnected(false);
          setLoading(false);
          
          // If token is invalid, clear it from localStorage to force re-login
          if (err.message && (err.message.includes('Authentication error') || err.message.includes('Invalid token'))) {
            logger.log('Invalid token detected, clearing authentication');
            localStorage.removeItem('token');
            window.location.reload(); // Force reload to trigger re-authentication
          }
        });
        
        // Connect if not already connected
        if (!socketInstance.connected) {
          socketInstance.connect();
        }
      } else {
        logger.log('No token found for socket authentication');
        setLoading(false);
      }
    } else {
      // Not authenticated, clean up socket state
      setRunningSyncs([]);
      setSyncHistory([]);
      setLoading(false);
    }
    
    // Cleanup function
    return () => {
      if (socketInstance) {
        logger.log('Cleaning up socket connection');
        socketInstance.off('connect');
        socketInstance.off('disconnect');
        socketInstance.off('reconnect_attempt');
        socketInstance.off('reconnect');
        socketInstance.off('reconnect_error');
        socketInstance.off('reconnect_failed');
        socketInstance.off('sync:initialStatus');
        socketInstance.off('sync:statusUpdate');
        socketInstance.off('sync:started');
        socketInstance.off('sync:progress');
        socketInstance.off('sync:completed');
        socketInstance.off('connect_error');
        socketInstance.disconnect();
      }
    };
  }, [isAuthenticated]); // removed runningSyncs from deps to avoid infinite loops

  return {
    socket,
    isConnected,
    error,
    loading,
    runningSyncs,
    syncHistory,
    setLoading,
    setError
  };
};

